import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { TradeablePlan } from '@/client/types/trade';
import {
  PromotionPricingResponse,
  PROMOTION_TOTIDS,
  DEFAULT_CCODE,
} from '@/client/types/promotion-pricing';

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const promotionPricingKeys = {
  all: ['promotion-pricing'] as const,
  lists: () => [...promotionPricingKeys.all, 'list'] as const,
  list: (totids: string, ccode: string) =>
    [...promotionPricingKeys.lists(), totids, ccode] as const,
};

// ─── Promotion plans + prices ──────────────────────────────────────────────────

const usePromotionPricingFn = () => {
  const request = useClientStore((s) => s.request);

  return withErrorHandling(
    async (
      totids: string = PROMOTION_TOTIDS,
      ccode: string = DEFAULT_CCODE,
    ): Promise<TradeablePlan[]> => {
      const params = new URLSearchParams({
        totid: totids,
        include_prices: '1',
        ...(ccode ? { ccode } : {}),
      });
      const response = await request<PromotionPricingResponse>(
        `/api/admin/tradeable_objects/?${params.toString()}`,
      );

      // Catalog is nested under data.tradeable_objects; the backend ignores the
      // `totid` filter and returns the full catalog, so narrow it client-side.
      const all = response.data?.tradeable_objects ?? [];
      const wanted = new Set(
        totids.split(',').map((t) => Number(t.trim())).filter((n) => !Number.isNaN(n)),
      );
      return wanted.size ? all.filter((p) => wanted.has(p.totid)) : all;
    },
    'An unexpected error occurred while fetching promotion pricing',
  );
};

export const usePromotionPricingQuery = (
  totids: string = PROMOTION_TOTIDS,
  ccode: string = DEFAULT_CCODE,
) => {
  const queryFn = usePromotionPricingFn();
  return useQuery({
    queryKey: promotionPricingKeys.list(totids, ccode),
    queryFn: () => queryFn(totids, ccode),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};
